<template>
  <multiselect
    :value="selectedOptions"
    :options="options"
    :multiple="true"
    :searchable="true"
    :show-labels="false"
    :allow-empty="true"
    :max-height="240"
    :limit="tagLimit"
    :limit-text="(count) => $t('flexigo.ridership.moreRoutes', [count])"
    label="name"
    track-by="name"
    class="min-w-250p w-auto"
    :placeholder="noRoutes ? $t('flexigo.ridership.noRoutesInRouteGroups') : $t('flexigo.ridership.allRoutes')"
    @input="onInput"
  >
    <span slot="noOptions">
      {{ $t('flexigo.ridership.noData') }}
    </span>
  </multiselect>
</template>

<script>
import { computed } from 'vue';
import Multiselect from 'vue-multiselect';

/**
 * The dashboards' route filter (Number of Riders, Occupancy, Ridership), working in route ids. Same-named
 * routes (loop routes, or the same route in several route groups) are listed once; picking that row selects
 * every id behind it. No selection means every route, unless `noRoutes` says the picked route groups have none.
 */
export default {
  name: 'RouteMultiselect',
  components: { Multiselect },
  props: {
    // [{ id, name }]
    routes: {
      type: Array,
      default: () => [],
    },
    selectedRouteIds: {
      type: Array,
      default: () => [],
    },
    // Narrow the list to one direction (a route's `direction`) or working type (`workingType`, matched in
    // upper case: 'Dynamic' lists the DYNAMIC routes). 'ALL' / 'All' lists every route.
    direction: {
      type: String,
      default: 'ALL',
    },
    workingType: {
      type: String,
      default: 'All',
    },
    // The picked route groups have no routes, so an empty selection means no data, not every route; the
    // placeholder says so instead of "All Routes".
    noRoutes: {
      type: Boolean,
      default: false,
    },
    // Selected rows shown as tags before the rest collapse into "+N more".
    tagLimit: {
      type: Number,
      default: 3,
    },
  },
  emits: ['change'],
  setup(props, { emit }) {
    const isAll = (value) => !value || value.toUpperCase() === 'ALL';
    const shownRoutes = computed(() =>
      props.routes.filter(
        ({ direction, workingType }) =>
          (isAll(props.direction) || direction === props.direction) &&
          (isAll(props.workingType) || workingType === props.workingType.toUpperCase()),
      ),
    );

    // One row per name: { name, ids }.
    const options = computed(() => {
      const byName = new Map();
      shownRoutes.value.forEach(({ id, name }) => {
        if (!byName.has(name)) byName.set(name, { name, ids: [] });
        byName.get(name).ids.push(id);
      });
      return Array.from(byName.values());
    });

    const selectedIdSet = computed(() => new Set(props.selectedRouteIds));

    // Derived rather than stored, so a selection set before the routes arrive shows up once they do.
    const selectedOptions = computed(() =>
      options.value.filter(({ ids }) => ids.some((id) => selectedIdSet.value.has(id))),
    );

    // A row that was already selected keeps only the ids it had (a preset may have narrowed it to some of
    // its same-named routes); a newly picked row brings all of its ids.
    function onInput(rows) {
      const routeIds = (rows || []).flatMap(({ ids }) => {
        const keptIds = ids.filter((id) => selectedIdSet.value.has(id));
        return keptIds.length ? keptIds : ids;
      });
      emit('change', routeIds);
    }

    return { options, selectedOptions, onInput };
  },
};
</script>
